import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, GitBranch, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { editorPublicationApi } from '@/lib/api';

const blankGenre = () => ({
  slug: '', name: '', blurb: '', parent_slug: '', sort_order: 0, enabled: true,
});

export default function EditorGenreTreePanel() {
  const [tree, setTree] = useState({ roots: [], genres: [] });
  const [form, setForm] = useState(blankGenre());
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setTree(await editorPublicationApi.genreTree()); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Genre tree could not load.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const parentOptions = useMemo(
    () => tree.genres.filter((row) => row.slug !== form.slug),
    [tree.genres, form.slug],
  );
  const flatRows = useMemo(() => flattenNodes(tree.roots), [tree.roots]);
  const choose = (row) => {
    setEditing(true);
    setForm({
      slug: row.slug, name: row.name, blurb: row.blurb || '',
      parent_slug: row.parent_slug || '', sort_order: row.sort_order || 0,
      enabled: row.enabled !== false,
    });
  };
  const reset = () => { setEditing(false); setForm(blankGenre()); };
  const save = async () => {
    try {
      const payload = {
        ...form,
        parent_slug: form.parent_slug || null,
        sort_order: Number(form.sort_order || 0),
      };
      const result = editing
        ? await editorPublicationApi.updateGenre(form.slug, payload)
        : await editorPublicationApi.createGenre(payload);
      setTree(result.tree);
      if (result.child_sync?.ok) toast.success('Genre saved and mirrored flat to Indie Reader.');
      else toast.warning('Genre saved; Indie Reader mirror needs attention.');
      reset();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Genre could not be saved.');
    }
  };
  const remove = async () => {
    if (!editing || !window.confirm(`Delete ${form.name}?`)) return;
    try {
      const result = await editorPublicationApi.deleteGenre(form.slug);
      setTree(result.tree);
      toast.success('Genre removed.');
      reset();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Genre could not be removed.');
    }
  };

  return (
    <section className="min-w-0 space-y-4 pb-6" data-testid="editor-genre-tree-panel">
      <GenreTreeHeader loading={loading} onRefresh={load} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 border border-border bg-card" data-testid="editor-genre-tree-list">
          {flatRows.map(({ row, depth }) => (
            <GenreTreeRow key={row.slug} row={row} depth={depth} onSelect={choose} />
          ))}
          {!tree.roots.length && !loading && (
            <div className="p-8 text-center text-[11.5px] text-muted-foreground">No genre nodes yet.</div>
          )}
        </div>
        <GenreForm
          form={form}
          editing={editing}
          parentOptions={parentOptions}
          onChange={setForm}
          onSave={save}
          onCancel={reset}
          onDelete={remove}
        />
      </div>
    </section>
  );
}

const GenreTreeHeader = ({ loading, onRefresh }) => (
  <div className="flex items-start justify-between gap-3">
    <div>
      <h1 className="flex items-center gap-2 text-[17px] font-semibold">
        <GitBranch className="h-5 w-5 text-primary" />Genre Tree
      </h1>
      <p className="mt-1 text-[11.5px] text-muted-foreground">
        The parent keeps the hierarchy. Indie Reader mirrors every node as a flat genre until its schema supports nesting.
      </p>
    </div>
    <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="editor-genre-tree-refresh">
      <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
    </Button>
  </div>
);

const GenreForm = ({ form, editing, parentOptions, onChange, onSave, onCancel, onDelete }) => (
  <aside className="h-fit border border-border bg-card p-4" data-testid="editor-genre-form">
    <div className="mb-3 text-[12px] font-semibold">{editing ? 'Edit genre node' : 'Add genre node'}</div>
    <Field label="Name">
      <Input value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} data-testid="editor-genre-name" />
    </Field>
    <Field label="Slug" className="mt-3">
      <Input
        value={form.slug}
        disabled={editing}
        onChange={(event) => onChange({ ...form, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
        data-testid="editor-genre-slug"
      />
    </Field>
    <Field label="Parent" className="mt-3">
      <select
        value={form.parent_slug}
        onChange={(event) => onChange({ ...form, parent_slug: event.target.value })}
        className="h-9 w-full border border-border bg-background px-2 text-[11.5px]"
        data-testid="editor-genre-parent"
      >
        <option value="">Top-level genre</option>
        {parentOptions.map((row) => <option key={row.slug} value={row.slug}>{row.name}</option>)}
      </select>
    </Field>
    <Field label="Order" className="mt-3">
      <Input type="number" value={form.sort_order} onChange={(event) => onChange({ ...form, sort_order: Number(event.target.value) })} data-testid="editor-genre-order" />
    </Field>
    <Field label="Description" className="mt-3">
      <textarea
        value={form.blurb}
        onChange={(event) => onChange({ ...form, blurb: event.target.value })}
        rows="4"
        className="w-full border border-border bg-background px-3 py-2 text-[11.5px]"
        data-testid="editor-genre-blurb"
      />
    </Field>
    <label className="mt-3 inline-flex items-center gap-2 text-[11px]">
      <input type="checkbox" checked={form.enabled} onChange={(event) => onChange({ ...form, enabled: event.target.checked })} data-testid="editor-genre-enabled" />
      Visible genre card
    </label>
    <div className="mt-4 flex gap-2">
      <Button size="sm" onClick={onSave} disabled={!form.name.trim() || !form.slug.trim()} data-testid="editor-genre-save">
        {editing ? <Save className="mr-1 h-3.5 w-3.5" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
        {editing ? 'Save node' : 'Add node'}
      </Button>
      {editing && <Button size="sm" variant="outline" onClick={onCancel} data-testid="editor-genre-cancel">Cancel</Button>}
      {editing && <Button size="sm" variant="ghost" onClick={onDelete} className="ml-auto text-destructive" data-testid="editor-genre-delete"><Trash2 className="h-3.5 w-3.5" /></Button>}
    </div>
  </aside>
);

const Field = ({ label, className = '', children }) => (
  <label className={`block text-[10px] uppercase text-muted-foreground ${className}`}>
    <span className="mb-1 block">{label}</span>{children}
  </label>
);

const GenreTreeRow = ({ row, depth, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(row)}
    className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left hover:bg-secondary/50"
    style={{ paddingLeft: `${12 + depth * 22}px` }}
    data-testid={`editor-genre-node-${row.slug}`}
  >
    <ChevronRight className={`h-3 w-3 ${row.children?.length ? 'text-primary' : 'text-muted-foreground/35'}`} />
    <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium">{row.name}</span>
    <span className="max-w-[42%] truncate font-mono text-[9.5px] text-muted-foreground">{row.slug}</span>
    {row.parent_slug && <span className="hidden rounded-full bg-muted px-1.5 py-0.5 text-[8.5px] uppercase sm:inline-flex">sub-genre</span>}
  </button>
);

function flattenNodes(nodes, depth = 0) {
  const rows = [];
  (nodes || []).forEach((row) => {
    rows.push({ row, depth });
    rows.push(...flattenNodes(row.children || [], depth + 1));
  });
  return rows;
}